<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import JobAnalysisToast from "@/components/JobAnalysisTracker/JobAnalysisToast.vue";
import SuccessToast from "@/components/SuccessToast.vue";
import { useStore } from "@/stores";
import type {
  JobAnalysisEntry,
  ToastItem,
} from "@/stores/job_analysis_tracker";
import {
  openJobAnalysisEntryPoint,
  openPositionAiSetup,
} from "@/utils/position_navigation";

const MAX_VISIBLE = 3;
const SUCCESS_TOAST_MS = 10_000;

const store = useStore();
const router = useRouter();

const agentName = computed<string>(
  () => store.getters["subscription/agentName"],
);
const entries = computed<JobAnalysisEntry[]>(
  () => store.getters["jobAnalysisTracker/orderedEntries"],
);
const slowEntryIds = computed<number[]>(
  () => store.getters["jobAnalysisTracker/slowEntryIds"],
);
const toastQueue = computed<ToastItem[]>(
  () => store.getters["jobAnalysisTracker/toastQueue"],
);

const expanded = ref(false);
const visibleEntries = computed(() =>
  expanded.value ? entries.value : entries.value.slice(0, MAX_VISIBLE),
);
const hiddenCount = computed(() =>
  expanded.value ? 0 : Math.max(0, entries.value.length - MAX_VISIBLE),
);

watch(
  () => entries.value.length,
  (length) => {
    if (length <= MAX_VISIBLE) expanded.value = false;
  },
);

const activeToast = computed<ToastItem | undefined>(() => toastQueue.value[0]);

const advanceToast = () => {
  store.commit("jobAnalysisTracker/shiftToast");
};

const openEntryPoint = async (entry: JobAnalysisEntry) => {
  await openJobAnalysisEntryPoint(router, entry);
  await store.dispatch("jobAnalysisTracker/dismiss", entry.id);
};

const openPosition = () => {
  const toast = activeToast.value;
  if (toast) openPositionAiSetup(router, toast.positionId);
  advanceToast();
};
</script>

<template>
  <div class="job-analysis-toast-stack" role="status" aria-live="polite">
    <JobAnalysisToast
      v-for="entry in visibleEntries"
      :key="entry.id"
      :entry="entry"
      :slow="slowEntryIds.includes(entry.id)"
      :agent-name="agentName"
      @dismiss="store.dispatch('jobAnalysisTracker/dismiss', entry.id)"
      @open-entry-point="openEntryPoint(entry)"
      @resume="store.dispatch('jobAnalysisTracker/resume', entry.id)"
    />
    <button
      v-if="entries.length > MAX_VISIBLE"
      type="button"
      class="job-analysis-toast-stack__more"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      {{ expanded ? "Show fewer jobs" : `+${hiddenCount} more jobs` }}
    </button>
  </div>
  <SuccessToast
    :key="activeToast?.positionId"
    :show="activeToast !== undefined"
    :message="activeToast ? `Position created: ${activeToast.title}` : ''"
    variant="success"
    placement="bottom"
    action-label="Open position"
    action-style="button"
    :auto-close-ms="SUCCESS_TOAST_MS"
    @close="advanceToast"
    @action="openPosition"
  />
</template>

<style lang="scss" scoped>
@use "sass:math";
@use "@/assets/styles/design-tokens" as *;

.job-analysis-toast-stack {
  position: fixed;
  // Leave room for the top bar and the completion notification below.
  top: $spacing-6 * 3 + $spacing-3;
  max-height: calc(100dvh - #{$spacing-6 * 3 + $spacing-3} - #{$spacing-6 * 6});
  overflow-y: auto;
  overscroll-behavior: contain;
  right: $spacing-6;
  // One layer under the import modal (10000) so modals cover it, and under the top bar (9999)
  // so New job is never covered.
  z-index: 9800;
  width: $size-modal-sm * 0.75;
  display: flex;
  flex-direction: column;
  gap: $spacing-3;

  > * {
    flex-shrink: 0;
  }

  @media (max-width: ($size-modal-sm * 1.6 - math.div($spacing-1, 4))) {
    top: $spacing-4 * 4 + $spacing-3;
    right: $spacing-3;
    width: calc(100vw - #{$spacing-3} * 2);
  }
}

.job-analysis-toast-stack__more {
  @include txt-small-plus;
  position: sticky;
  bottom: 0;
  align-self: flex-end;
  padding: $spacing-1-5 $spacing-3;
  background-color: $color-surface-default;
  border: calc($spacing-1 / 4) solid $color-border-default;
  border-radius: $radius-full;
  box-shadow: $shadow-modal;
  color: $color-content-default;
  cursor: pointer;

  &:hover {
    background-color: $color-surface-background;
  }

  &:focus-visible {
    @include focus-ring;
  }
}
</style>
